import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Crown, LogOut, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { TeamManagement } from './TeamManagement';

// Enhanced UserMenu component with better design
const UserMenu = ({ userName, userEmail, onLogout }: { userName: string, userEmail: string, onLogout: () => void }) => (
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-semibold text-sm shadow-lg ring-2 ring-white/10">
        {userName.charAt(0).toUpperCase()}
      </div>
      <div className="hidden lg:block">
        <div className="text-sm font-semibold text-slate-900">{userName}</div>
        <div className="text-xs text-slate-500">{userEmail}</div>
      </div>
    </div>
    <div className="h-8 w-px bg-slate-200"></div>
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={onLogout}
      className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl px-3 py-2 transition-all duration-200"
    >
      <LogOut size={16} />
      <span className="hidden sm:inline ml-2">Sign Out</span>
    </Button>
  </div>
);

export function DashboardHeader() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const {
    organization,
    isOrganizationMember,
    organizationRole,
  } = useSubscription();

  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (user) {
      setUserName(user.user_metadata?.full_name || user.email?.split('@')[0] || '');
      setUserEmail(user.email || '');
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50 shadow-sm">
      <div className="max-w-[1800px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section - Logo and Navigation */}
          <div className="flex items-center gap-8">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-3 text-slate-900 hover:text-slate-700 transition-colors duration-200"
            >
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-lg ring-2 ring-white/10">
                <span className="text-white font-bold text-sm">
                  {isOrganizationMember && organization ? 
                    organization.organizationName.charAt(0).toUpperCase() : 
                    'RM'
                  }
                </span>
              </div>
            </Link>
            
            {/* Organization indicator - cleaner design */}
            {isOrganizationMember && organization && (
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-200/60 shadow-sm">
                <Building2 size={14} className="text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  {organization.organizationName}
                </span>
                {organizationRole === 'admin' && (
                  <Crown size={12} className="text-amber-500 ml-1" />
                )}
              </div>
            )}
          </div>

          {/* Right section - Actions and User Menu */}
          <div className="flex items-center gap-4">
            {/* License Management for Admin */}
            {isOrganizationMember && organizationRole === 'admin' && organization && (
              <div className="hidden xl:flex items-center gap-3 px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-200/60 shadow-sm">
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">
                    {organization.licenses_used}/{organization.licenses_purchased}
                  </div>
                  <div className="text-xs text-slate-500">Licenses</div>
                </div>
              </div>
            )}

            {/* Team Management for Admin */}
            {isOrganizationMember && organizationRole === 'admin' && (
              <TeamManagement />
            )}











            {/* User Menu */}
            <UserMenu
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            />
          </div>
        </div>
      </div>
    </header>
  );
}